import { FetchStrategy, LogLevels } from '@powersync/common';
import { AbortOperation } from '../../../utils/AbortOperation.js';
import { doneResult, extractBsonObjects, extractJsonLines } from '../../../utils/stream_transform.js';
import { EventQueue } from '../../../utils/async.js';
import { POWERSYNC_JS_VERSION } from '../../../version.js';
const POWERSYNC_TRAILING_SLASH_MATCH = /\/+$/;
const webSocketPlatform = {
    LogLevels,
    EventQueue,
    AbortOperation
};
export function lazyFetchImplementation(getFetch) {
    let resolved;
    return (request) => {
        const fetch = (resolved ??= getFetch());
        return fetch(request);
    };
}
/**
 * @internal
 */
export class AbstractRemote {
    connector;
    logger;
    credentials = null;
    constructor(connector, logger) {
        this.connector = connector;
        this.logger = logger;
    }
    /**
     * Get credentials currently cached, or fetch new credentials if none are
     * available.
     *
     * These credentials may have expired already.
     */
    async getCredentials(signal) {
        if (this.credentials) {
            return this.credentials;
        }
        return this.prefetchCredentials(signal);
    }
    /**
     * Fetch a new set of credentials and cache it.
     *
     * Until this call succeeds, `getCredentials` will still return the
     * old credentials.
     *
     * This may be called before the current credentials have expired.
     */
    async prefetchCredentials(signal) {
        this.credentials = await this.fetchCredentials(signal);
        return this.credentials;
    }
    /**
     * Get credentials for PowerSync.
     *
     * This should always fetch a fresh set of credentials - don't use cached
     * values.
     */
    async fetchCredentials(signal) {
        const credentials = await this.connector.fetchCredentials(signal);
        if (credentials?.endpoint.match(POWERSYNC_TRAILING_SLASH_MATCH)) {
            throw new Error(`A trailing forward slash "/" was found in the fetchCredentials endpoint: "${credentials.endpoint}". Remove the trailing forward slash "/" to fix this error.`);
        }
        return credentials;
    }
    /***
     * Immediately invalidate credentials.
     *
     * This may be called when the current credentials have expired.
     */
    invalidateCredentials() {
        this.credentials = null;
        this.connector.invalidateCredentials?.();
    }
    getUserAgent() {
        return `powersync-js/${POWERSYNC_JS_VERSION}`;
    }
    async buildRequest(path, signal) {
        const credentials = await this.getCredentials(signal);
        if (credentials != null && (credentials.endpoint == null || credentials.endpoint == '')) {
            throw new Error('PowerSync endpoint not configured');
        }
        else if (credentials?.token == null || credentials?.token == '') {
            const error = new Error(`Not signed in`);
            error.status = 401;
            throw error;
        }
        const userAgent = this.getUserAgent();
        return {
            url: credentials.endpoint + path,
            headers: {
                'content-type': 'application/json',
                Authorization: `Token ${credentials.token}`,
                'x-user-agent': userAgent
            },
            userAgent,
            path
        };
    }
    async fetchAndDecodeJson({ path, method = 'GET', headers, body, signal }) {
        const request = await this.buildRequest(path, signal);
        const res = await this.fetch({
            resource: request.url,
            request: {
                method,
                headers: {
                    ...headers,
                    ...request.headers
                },
                body,
                signal
            },
            expectStreamingResponse: false
        });
        if (res.status === 401) {
            this.invalidateCredentials();
        }
        if (!res.ok) {
            throw new Error(`Received ${res.status} - ${res.statusText} when getting from ${path}: ${await res.text()}}`);
        }
        return res.json();
    }
    /**
     * @returns A text decoder decoding UTF-8. This is a method to allow patching it for Hermes which doesn't support the
     * builtin, without forcing us to bundle a polyfill with `@powersync/common`.
     */
    createTextDecoder() {
        return new TextDecoder();
    }
    createSocket(url) {
        return new WebSocket(url);
    }
    /**
     * Returns a data stream of sync line data, fetched via RSocket-over-WebSocket.
     *
     * The only mechanism to abort the returned stream is to use the abort signal in {@link SocketSyncStreamOptions}.
     */
    async socketStreamRaw(options) {
        const support = await this.loadWebSocketSupport(webSocketPlatform);
        const request = await this.buildRequest(options.path, options.abortSignal);
        request.url = request.url.replace(/^https?:\/\//, function (match) {
            return match === 'https://' ? 'wss://' : 'ws://';
        });
        const { fetchStrategy = FetchStrategy.Buffered, abortSignal, data } = options;
        return await support.webSocketSyncStream({
            remote: this,
            buffered: fetchStrategy == FetchStrategy.Buffered,
            abortSignal,
            requestPayload: data,
            request
        });
    }
    /**
     * Posts a `/sync/stream` request, asserts that it completes successfully and returns the streaming response as an
     * async iterator of byte blobs.
     *
     * To cancel the async iterator, use the abort signal from {@link SyncStreamOptions} passed to this method.
     */
    async fetchStreamRaw(options) {
        const { data, path, abortSignal } = options;
        const request = await this.buildRequest(path, abortSignal);
        /**
         * This abort controller will abort pending fetch requests.
         * If the request has resolved, it will be used to close the readable stream.
         * Which will cancel the network request.
         *
         * This nested controller is required since:
         *  Aborting the active fetch request while it is being consumed seems to throw
         *  an unhandled exception on the window level.
         */
        if (abortSignal.aborted) {
            throw new AbortOperation('Abort request received before making fetchStreamRaw request');
        }
        const controller = new AbortController();
        let reader = null;
        abortSignal.addEventListener('abort', () => {
            const reason = abortSignal.reason ??
                new AbortOperation('Cancelling network request before it resolves. Abort signal has been received.');
            if (reader == null) {
                // Only abort via the abort controller if the request has not resolved yet
                controller.abort(reason);
            }
            else {
                reader.cancel(reason).catch(() => {
                    // Cancelling the reader might rethrow an exception we would have handled by throwing in next(). So we can
                    // ignore it here.
                });
            }
        });
        let res;
        let responseIsBson = false;
        try {
            const ndJson = 'application/x-ndjson';
            const bson = 'application/vnd.powersync.bson-stream';
            res = await this.fetch({
                resource: request.url,
                request: {
                    method: 'POST',
                    headers: {
                        ...request.headers,
                        accept: `${bson};q=0.9,${ndJson};q=0.8`
                    },
                    body: JSON.stringify(data),
                    signal: controller.signal,
                    cache: 'no-store'
                },
                expectStreamingResponse: true
            });
            if (!res.ok || !res.body) {
                const text = await res.text();
                const error = new Error(`HTTP ${res.statusText}: ${text}`);
                error.status = res.status;
                this.logger.log({
                    level: LogLevels.error,
                    message: `Could not POST streaming to ${path} - ${res.status} - ${res.statusText}: ${text}`,
                    error
                });
                if (res.status === 401) {
                    this.invalidateCredentials();
                }
                throw error;
            }
            const contentType = res.headers.get('content-type');
            responseIsBson = contentType == bson;
        }
        catch (ex) {
            if (ex.name == 'AbortError') {
                throw new AbortOperation(`Pending fetch request to ${request.url} has been aborted.`);
            }
            throw ex;
        }
        reader = res.body.getReader();
        const stream = {
            next: async () => {
                if (controller.signal.aborted) {
                    return doneResult;
                }
                try {
                    return await reader.read();
                }
                catch (ex) {
                    if (controller.signal.aborted) {
                        // .read() completes with an error if we cancel the reader, which we do to disconnect. So this is just
                        // things working as intended, we can return a done event and consider the exception handled.
                        return doneResult;
                    }
                    throw ex;
                }
            }
        };
        return { isBson: responseIsBson, stream };
    }
    /**
     * Posts a `/sync/stream` request.
     *
     * Depending on the `Content-Type` of the response, this returns strings for sync lines or encoded BSON documents as
     * `Uint8Array`s.
     */
    async fetchStream(options) {
        const { isBson, stream } = await this.fetchStreamRaw(options);
        if (isBson) {
            return extractBsonObjects(stream);
        }
        else {
            return extractJsonLines(stream, this.createTextDecoder());
        }
    }
}
//# sourceMappingURL=AbstractRemote.js.map